import React, { useEffect, useRef, useState } from 'react';
import { fetchAuditoriaConfiguracoes } from '../../../api/orcamentoCiliaApi';
import { AuditoriaConfiguracaoItem, rotuloDoAutor } from '../../../interfaces/OrcamentoCilia';
import '../../../components/modal/planning-comparison/PlanningComparisonModal.css';
import '../../auditoria/components/AuditoriaCiliaModais.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Histórico de alterações da CONFIGURAÇÃO do módulo (at-05, 16/08/2026).
 *
 * Motor separado da timeline de orçamentos, de propósito (molde do `HistoricoAlteracoes` do
 * SOAC, at-69): aqui a pergunta é só "quem mudou o quê, de que para quê" — sem cadeia, sem
 * peça, sem modal por tipo de evento.
 *
 * **Por que importa**: os defaults decidem o resultado de toda importação seguinte (NCM
 * padrão, janela da média de compras, margem do alerta de preço) e o de-para decide o
 * reconhecimento das peças. Mudança silenciosa aqui aparece como comportamento estranho
 * lá na frente, sem ninguém lembrar que alguém trocou um valor.
 */

interface Props {
  onFechar: () => void;
}

const HistoricoConfiguracaoModal: React.FC<Props> = ({ onFechar }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dados, setDados] = useState<AuditoriaConfiguracaoItem[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const cliqueFora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onFechar]);

  useEffect(() => {
    let ativo = true;
    fetchAuditoriaConfiguracoes(1, 50)
      .then((r) => {
        if (!ativo) return;
        setDados(r.data);
        setErro(null);
      })
      .catch(() => {
        if (!ativo) return;
        // "Nenhuma alteração registrada" é a afirmação mais séria desta tela e só pode
        // sair quando a consulta responde (lição do at-69).
        setDados(null);
        setErro('Não foi possível carregar o histórico de alterações. Tente novamente; se persistir, acione a TI.');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={ref} role="dialog" aria-modal="true" aria-label="Histórico de alterações">
        <div className="planning-modal-header">
          <h2>Histórico de alterações</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-body">
          {carregando && <div className="cilia-hist-vazio">Carregando o histórico...</div>}

          {erro && (
            <div className="erro-de-tela" role="alert">
              {erro}
            </div>
          )}

          {!carregando && !erro && dados && dados.length === 0 && (
            <div className="cilia-hist-vazio">Nenhuma alteração registrada.</div>
          )}

          {!erro && dados && dados.length > 0 && (
            <div className="cilia-hist-lista">
              {dados.map((linha) => (
                <div key={linha.id} className="cilia-hist-item">
                  <div className="cilia-hist-topo">
                    <span className="cilia-hist-titulo">
                      {linha.acao}
                      {linha.alvo ? `: ${linha.alvo}` : ''}
                    </span>
                    <span className="cilia-hist-data">
                      {new Date(linha.em).toLocaleDateString('pt-BR')}{' '}
                      {new Date(linha.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {linha.detalhes.length > 0 && (
                    <div className="cilia-hist-detalhes">
                      {linha.detalhes.map((d, i) => (
                        <div key={i}>
                          {d.rotulo}: <strong>{d.de}</strong> para <strong>{d.para}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="cilia-hist-autor" title={rotuloDoAutor(linha).title}>Por: {rotuloDoAutor(linha).texto}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricoConfiguracaoModal;
